import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Row, Typography, Card, Tabs, Space, Form, DatePicker, Radio, InputNumber, Input, message, Alert } from 'antd';
import { TagOutlined } from '@ant-design/icons';
import { RESOURCE } from '../../config/constants/alerts';
import CouponDetailTable from './CouponDetailTable';
import { GET_COUPON_LIST, COUPON_LIST_LOADED, CREATE_COUPON, COUPON_IS_CREATED } from '../../state/coupon/types';
import ModalComponent from '../../components/modals/ModalComponent';
import locale from 'antd/es/date-picker/locale/es_ES';
import moment from 'moment';
import short from 'short-uuid';
import { STATUS } from '../../config/constants/states';
import rutValidator from '../../helpers/rutValidator';
import PropTypes from 'prop-types';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const CouponView = ({
  getCouponList,
  createCoupon,
  isCreated,
  resetCreationStatus,
  errMessage
}) => {

  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalSpinning, setModalSpinning] = useState(false);

  useEffect(() => {
    if (isCreated === STATUS.SUCCESS) {
      setModalSpinning(false);
      setModalVisible(false);
      resetCreationStatus();
      form.resetFields();
      message.success('Código de descuento creado satisfactoriamente', 10);
      getCouponList({ page: 1, limit: 10 });
    }
  }, [isCreated]);

  const newCoupon = (data) => {
    setModalSpinning(true);
    createCoupon({
      ...data,
      expirationDate: new Date(data.expirationDate).toISOString(),
      whiteList: [data.whiteList],
    });
  };

  const CreateCouponForm = () => {
    const couponCode = short.uuid();
    const [amountType, setAmountType] = useState('amountOff');
    const [viewCodeError, showCodeError] = useState(false);
    const disabledDate = current => (current && current < moment().endOf('day'));

    useEffect(() => {
      if (isCreated === STATUS.ERROR) {
        if (errMessage !== '') {
          if (errMessage.includes('duplicate key error')) {
            setModalSpinning(false);
            showCodeError(true);
          } else {
            setModalSpinning(false);
            setModalVisible(false);
            resetCreationStatus();
          }
        }
      }
    }, [isCreated]);

    const inputValidate = (e) => {
      let inputName = e.target.id;
      let value = e.target.value.replace(/[^\w\s-]+/g, '');
      form.setFieldsValue({ [inputName]: value });
    };

    return (
      <React.Fragment>
        {viewCodeError &&
          <Col className="position-relative mb-4">
            <Alert message="Código de cupón ingresado ya se encuentra en uso" type="error" />
          </Col>
        }
        <Form
          form={form}
          layout="vertical"
          onFinish={createCoupon}
          autoComplete="on"
        >
          <Col className="position-relative">
            <Form.Item
              label="Rut de beneficiario"
              name="whiteList"
              rules={[
                ({
                  validator(_, value) {
                    if (value && rutValidator(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      'Debe ingresar un rut válido',
                    );
                  },
                }),
                {
                  required: true,
                  message: 'Campo Rut es obligatorio',
                },
                {
                  pattern: new RegExp(/^[0-9]+[-|‐]{1}[0-9kK]{1}$/),
                  message: 'Debe ingresar rut con guión'
                }
              ]}
            >
              <Input size="large" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col className="position-relative">
            <Form.Item
              label="Fecha de expiración"
              name="expirationDate"
              rules={[
                { required: true, message: 'Debes ingresar fecha de expiración' },
              ]}
            >
              <DatePicker
                disabledDate={disabledDate}
                locale={locale}
                size="large"
                format="DD-MM-YYYY"
                style={{ width: '100%' }}
                placeholder="Selecciona una fecha"
              />
            </Form.Item>
          </Col>
          <Col className="position-relative">
            <Form.Item
              label="Activo"
              name="active"
              rules={[
                { required: true, message: 'Debes indicar si está activo o no' },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>Sí</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col className="position-relative">
            <Form.Item
              label="Cantidad de usos máximos"
              name="maxRedemptions"
              rules={[
                { required: true, message: 'Debes ingresar la cantidad máxima de usos' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                placeholder="Ingresa cantidad máxima de usos"
              />
            </Form.Item>
          </Col>
          <Col className="position-relative">
            <Form.Item
              label="Editar código de cupón (Opcional)"
              name="code"
              className="mb-0"
              initialValue={couponCode}
              rules={[
                { required: true, message: 'Debe ingrescar un código' },
              ]}
            >
              <Input size="large" autoComplete="off" placeholder="Ingresa código de cupón" onChange={inputValidate} />
            </Form.Item>
            <Text className="mb-4 d-block">*Código generado automaticamente. Si ingresa un código de forma manual no puede repetirse en otro cupón</Text>
          </Col>
          <Col className="position-relative">
            <Form.Item
              label="Tipo de descuento"
              name="type"
              rules={[
                { required: true, message: 'Debes indicar tipo' },
              ]}
            >
              <Radio.Group onChange={(option) => { setAmountType(option.target.value); }}>
                <Radio value='amountOff'>Monto fijo</Radio>
                <Radio value='percentageOff'>Porcentaje</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col className="position-relative">
            <Form.Item
              label="Monto de descuento"
              name={`${amountType}`}
              rules={[
                { required: true, message: 'Debes ingresar el monto de descuento' },
              ]}
            >
              {amountType === 'percentageOff' ?
                <InputNumber
                  size="large"
                  placeholder="Ingresa el porcentaje de descuento"
                  max={99}
                />
                : <InputNumber
                  size="large"
                  placeholder="Ingresa el monto de descuento"
                  max={32989}
                />
              }
            </Form.Item>
          </Col>
          <Col className="position-relative">
            <Form.Item
              label="Descripción"
              name="description"
            >
              <Input size="large" autoComplete="off"/>
            </Form.Item>
          </Col>
        </Form>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Card>
        <Row gutter={16}>
          <Col xs={24} sm={6} md={12}>
            <Title level={3}>Códigos promocionales</Title>
          </Col>
          <Col xs={24} sm={18} md={12} className="d-flex justify-content-end">
            <Space className="buttons-responsive">
              <ModalComponent
                modalVisible={modalVisible}
                title='Crear nuevo código promocional'
                okText='Crear'
                cancelText='Cancelar'
                onOk={() => {
                  form
                    .validateFields()
                    .then((values) => {
                      newCoupon(values);
                    });
                }}
                onCancel={() => setModalVisible(false)}
                spinning={modalSpinning}
                buttonLabel='Crear nuevo código promocional'
                buttonOnClick={() => setModalVisible(true)}
                content={<CreateCouponForm />}
                maskClosable={false}
              />
            </Space>
          </Col>
        </Row>
        <Tabs
          defaultActiveKey={activeTab}
          onChange={activeKey => setActiveTab(activeKey)}
        >
          <TabPane
            tab={
              <span>
                <TagOutlined />
                Códigos creados
              </span>
            }
            key="0"
          >
            <CouponDetailTable />
          </TabPane>

        </Tabs>
      </Card>
    </React.Fragment>
  );
};

CouponView.propTypes = {
  getCouponList: PropTypes.object.isRequired,
  createCoupon: PropTypes.func.isRequired,
  isCreated: PropTypes.bool.isRequired,
  resetCreationStatus: PropTypes.func.isRequired,
  errMessage: PropTypes.string.isRequired,
};

const data = state => ({
  isCreated: state.coupon.isCreated,
  errMessage: state.error.message
});

const actions = dispatch => ({
  getCouponList: (params) => dispatch({ type: GET_COUPON_LIST, payload: { ...params, resource: RESOURCE.GET_COUPON_LIST } }),
  createCoupon: (params) => dispatch({ type: CREATE_COUPON, payload: { params, resource: RESOURCE.CREATE_COUPON } }),
  resetStatus: () => dispatch({ type: COUPON_LIST_LOADED, payload: false }),
  resetCreationStatus: () => dispatch({ type: COUPON_IS_CREATED, payload: STATUS.PENDING }),
});

export default connect(data, actions)(CouponView);